import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Grid,
  MUIDataTable,
  SectionHeader,
  Typography,
  useDataTable,
} from 'web-library';
import { useHistory, useParams } from 'react-router-dom';
import FormHeader from '../../../components/form-items/FormHeader';
import { candidateService } from '../../../api';
import RouteKey from '../../../routes/key';
import {
  Translations,
  usePostSubjectValidation,
  useReviewSubjectColumn,
  useReviewSubjectDeleteColumn,
} from '../logic/useSubjectAmendment';
import { RegisteredAndDeletedSubjects } from '../../../api/candidate/subject/model';

const useStyles = makeStyles()((theme) =>
({
  textContainer: {
    marginBottom: theme.spacing(3),
  },
  subjectContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  subjectText: {
    width: '100%',
  },
  actionButton: {
    padding: 0,

    '& span': {
      justifyContent: 'flex-start',
    },
  },
  editContainer: {
    marginBottom: theme.spacing(6),
  },
  deleteButton: {
    color: '#E55934',
  },
  deleteContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  button: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
      marginTop: theme.spacing(1),
    },
  },
  optionalPapers: {
    color: theme.color.text.textTertiary,
  },
  actionTaken: {
    color: theme.color.text.textTertiary,
  },
  errorMsgContainer: {
    marginBottom: theme.spacing(1),
  },
  subjectValidationMsg: {
    fontWeight: 600,
    color: theme.color.state.stateError,
  },
}));

interface ReviewSubjectPathParams {
  uuid?: string;
}

const {
  examRegistrations,
  reviewParticulars,
  editRegistration,
  addSubject,
  coordinatorExamRegistrations,
  coordinatorAddSubject,
  coordinatorEditRegistration,
  coordinatorReviewParticulars,
} = RouteKey.amendment.child;
const { amendment } = RouteKey.child;

const ReviewSubject = ({
  translations,
  isGroup,
}: {
  translations: Translations;
  isGroup: boolean;
}) => {
  const { classes } = useStyles();

  const { t } = useTranslation();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable();

  const { uuid = '' } = useParams<ReviewSubjectPathParams>();

  const { data } = candidateService.subject.candidate.useGetAmendmentReviewSubject(uuid);
  const history = useHistory();

  const addSubjectUrl: string = isGroup
    ? `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorAddSubject.route}/${uuid}`
    : `/${amendment}/${examRegistrations.route}/${uuid}/${addSubject.route}`;

  const editRegistrationUrl: string = isGroup
    ? `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorEditRegistration.route}/${uuid}`
    : `/${amendment}/${examRegistrations.route}/${uuid}/${editRegistration.route}`;

  const reviewParticularsUrl: string = isGroup
    ? `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorReviewParticulars.route}/${uuid}`
    : `/${amendment}/${examRegistrations.route}/${uuid}/${reviewParticulars.route}`;

  const {
    subjectValidationMsg,
    postSubjectValidation,
    apiStatus,
    setApiStatus,
  } = usePostSubjectValidation(reviewParticularsUrl);

  const onConfirmClick = () => {
    postSubjectValidation.mutate(uuid);
    setApiStatus('loading');
  };

  const isDisplayConfirmAmendmentError =
    !data?.deletedSubjects.length &&
    !data?.registeredSubjects.some(
      (d: RegisteredAndDeletedSubjects) =>
        d.actionTaken === 'Added' ||
        d.actionTaken === 'Updated' ||
        d.optionalPapers.some((paper) => paper.status !== 'REGISTERED')
    );

  /** define Mui DataTable Column Definition
   * */
  const tableColumns = useReviewSubjectColumn(classes, translations, data);

  /** define Mui DataTable Delete Column Definition
   * */
  const deleteTableColumns = useReviewSubjectDeleteColumn(classes, translations, data);

  return (
    <>
      <SectionHeader title={t('amendment->>SubjectAmendment.Review Subject(s)')} />

      <Typography className={classes.textContainer}>
        {t('amendment->>ReviewSubject.Review and ensure that the amendments made are correct.')}
      </Typography>

      <Box className={classes.editContainer}>
        {data && (
          <MUIDataTable
            // @ts-ignore
            state={state}
            dispatch={tableDispatch}
            columns={tableColumns}
            // @ts-ignore
            data={data.registeredSubjects}
            options={{
              ...tOptions,
            }}
          />
        )}
      </Box>

      <FormHeader title={t('amendment->>EditSubject.Deleted Subject(s)')} />

      <Box className={classes.deleteContainer}>
        {data && (
          <MUIDataTable
            // @ts-ignore
            state={state}
            dispatch={tableDispatch}
            columns={deleteTableColumns}
            // @ts-ignore
            data={data.deletedSubjects}
            options={{
              ...tOptions,
            }}
          />
        )}
      </Box>

      <Box className={classes.errorMsgContainer}>
        {subjectValidationMsg.length > 0 &&
          subjectValidationMsg.map((errorMsg) => (
            <Typography
              id="errorMessage"
              key={errorMsg}
              className={classes.subjectValidationMsg}
              variant="body2"
            >
              {errorMsg}
            </Typography>
          ))}
      </Box>

      {isDisplayConfirmAmendmentError && (
        <Grid xs={12} md={8}>
          <Typography className={classes.errorText} display="inline" variant="body2">
            {t(
              'amendment->>ReviewSubject.No amendment has been made to the registered subject(s).'
            )}
          </Typography>
        </Grid>
      )}
      <Grid container xs={12} md={12} spacing={2}>
        <Grid xs={12} md={4}>
          <Button
            className={classes.button}
            type="button"
            color="secondary"
            variant="outlined"
            fullWidth
            id="button-edit-subject"
            onClick={() => {
              history.push(editRegistrationUrl);
            }}
          >
            {t('amendment->>ReviewSubject.Edit Registered Subject')}
          </Button>
        </Grid>
        <Grid xs={12} md={3}>
          <Button
            className={classes.button}
            type="button"
            color="secondary"
            variant="outlined"
            fullWidth
            id="button-addOrDelete-subject"
            onClick={() => {
              history.push(addSubjectUrl);
            }}
          >
            {t('amendment->>ReviewSubject.Add/Delete Subject')}
          </Button>
        </Grid>
        <Grid xs={12} md={4}>
          {isDisplayConfirmAmendmentError ? (
            <ApiButton
              className={classes.button}
              type="button"
              color="secondary"
              fullWidth
              disabled
              status={apiStatus}
              id="button-confirm-amendment"
            >
              {t('amendment->>ReviewSubject.Confirm Amendment')}
            </ApiButton>
          ) : (
            <ApiButton
              id="button-confirm-amendment"
              className={classes.button}
              type="button"
              color="secondary"
              fullWidth
              onClick={onConfirmClick}
              status={apiStatus}
            >
              {t('amendment->>ReviewSubject.Confirm Amendment')}
            </ApiButton>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ReviewSubject;
